import { sortedOptionsFor, unlocksAtTier } from './layer-stack'
import type { BoOption, TierRule } from './types'

export type Direction = 'earlier' | 'later'

export interface TierChange {
  slug: string
  displayName: string
  before: number | null
  after: number | null
}

export interface SortUpdate {
  slug: string
  sortOrder: number
}

export interface ReorderPlan {
  /** The rows to PATCH, each with the sortOrder it must end up with. */
  updates: SortUpdate[]
  /** Options whose unlocking tier moves as a result. Empty unless a `count` rule is active. */
  tierChanges: TierChange[]
  /** Options left sitting behind one that unlocks later. */
  orderBreaks: TierOrderBreak[]
  possible: boolean
}

const EMPTY: ReorderPlan = {
  updates: [],
  tierChanges: [],
  orderBreaks: [],
  possible: false,
}

/**
 * What moving one option would do, worked out before anything is sent.
 *
 * A `count` tier rule grants "the first N in sort order", so a move silently changes which tier
 * unlocks which option — including options nobody touched. That consequence is the whole reason
 * this returns `tierChanges` rather than just the new order.
 */
export function planReorder(
  options: BoOption[],
  sectionId: string,
  slug: string,
  direction: Direction,
  tierRule: TierRule | null,
): ReorderPlan {
  const ordered = sortedOptionsFor(options, sectionId) as BoOption[]
  const from = ordered.findIndex((o) => o.slug === slug)
  if (from < 0) {
    return EMPTY
  }
  const to = direction === 'earlier' ? from - 1 : from + 1
  if (to < 0 || to >= ordered.length) {
    return EMPTY
  }

  const moved = [...ordered]
  const [lifted] = moved.splice(from, 1)
  moved.splice(to, 0, lifted)
  const labelOf = (key: string) =>
    moved.find((o) => o.slug === key)?.displayName ?? key

  return {
    updates: minimalUpdates(ordered, moved),
    tierChanges: tierChangesBetween(ordered, moved, tierRule),
    orderBreaks: tierOrderBreaks(
      moved.map((o) => o.slug),
      tierRule,
      labelOf,
    ),
    possible: true,
  }
}

/** Only the rows whose position actually changed - a PATCH per untouched option is noise. */
function minimalUpdates(before: BoOption[], after: BoOption[]): SortUpdate[] {
  return after
    .map((option, index) => ({ option, index }))
    .filter(({ option, index }) => before[index]?.slug !== option.slug)
    .map(({ option, index }) => ({ slug: option.slug, sortOrder: index }))
}

/**
 * The tiers a reorder moves, for any positional list.
 *
 * Shared by options and colours: both unlock BY POSITION under a `count` ladder, so dragging one
 * to the front is not a cosmetic change - it hands it to a lower tier and pushes another up.
 */
export function tierChangesForKeys(
  before: string[],
  after: string[],
  rule: TierRule | null,
  labelOf: (key: string) => string = (key) => key,
): TierChange[] {
  // The `count` test is REDUNDANT and kept deliberately, exactly as in `tierChangesBetween`:
  // `unlocksAtTier` already ignores position for a `slugs` rule, so no test can tell this branch
  // from its absence. It states the intent and guards a future change to `unlocksAtTier`.
  if (!rule || rule.kind !== 'count') {
    return []
  }

  return after
    .map((key, index) => ({
      slug: key,
      displayName: labelOf(key),
      before: unlocksAtTier(rule, before.indexOf(key), key),
      after: unlocksAtTier(rule, index, key),
    }))
    .filter((change) => change.before !== change.after)
}

export interface TierOrderBreak {
  key: string
  displayName: string
  tier: number
  /** The earlier entry it now sits behind, which unlocks LATER than it does. */
  afterKey: string
  afterTier: number
}

/**
 * Entries that sit behind something unlocking at a higher tier.
 *
 * Not a correctness problem — the gate still works — but it reads as a mistake: a T0 colour after
 * a T5 one means the cheapest thing is buried behind the most expensive. Worth saying out loud,
 * because nothing else in the screen would ever mention it.
 */
export function tierOrderBreaks(
  keys: string[],
  rule: TierRule | null,
  labelOf: (key: string) => string = (key) => key,
): TierOrderBreak[] {
  if (!rule) {
    return []
  }
  const tiers = keys.map((key, index) => unlocksAtTier(rule, index, key) ?? 0)
  const breaks: TierOrderBreak[] = []
  let highest = { index: 0, tier: tiers[0] ?? 0 }

  keys.forEach((key, index) => {
    if (index === 0) {
      return
    }
    if (tiers[index] < highest.tier) {
      breaks.push({
        key,
        displayName: labelOf(key),
        tier: tiers[index],
        afterKey: keys[highest.index],
        afterTier: highest.tier,
      })
    } else {
      highest = { index, tier: tiers[index] }
    }
  })

  return breaks
}

/**
 * The list re-sorted so tiers climb, stably — within a tier nothing moves.
 *
 * Only for a `slugs` ladder: there the order is cosmetic, so a tier edit can leave a T0 entry
 * buried mid-list and sorting costs nothing. Under a `count` ladder position IS the tier, so
 * "fixing" the order would change what the editor just set.
 */
export function sortedByTier(keys: string[], rule: TierRule | null): string[] {
  if (!rule || rule.kind !== 'slugs') {
    return keys
  }
  const tierOf = (key: string) => rule.perSlug[key] ?? 0

  return [...keys].sort((a, b) => tierOf(a) - tierOf(b))
}

function tierChangesBetween(
  before: BoOption[],
  after: BoOption[],
  rule: TierRule | null,
): TierChange[] {
  // The `count` test is REDUNDANT today and kept deliberately: `unlocksAtTier` already ignores
  // position for a `slugs` rule, so no test can tell this branch from its absence. It states the
  // intent, and it is what stops a future change in `unlocksAtTier` from warning about a ladder
  // where position is cosmetic.
  if (!rule || rule.kind !== 'count') {
    return []
  }

  return after
    .map((option, index) => {
      const wasAt = before.findIndex((o) => o.slug === option.slug)

      return {
        slug: option.slug,
        displayName: option.displayName,
        before: unlocksAtTier(rule, wasAt, option.slug),
        after: unlocksAtTier(rule, index, option.slug),
      }
    })
    .filter((change) => change.before !== change.after)
}
